/**
 * Tauri PC device 등록 및 알림 설정 관리
 *
 * 로그아웃 후에도 익명 알림을 받기 위한 device_token 관리.
 * - 로그인 시: registerDevice() 호출 → 백엔드에 device 등록 + notifyEnabled 반환
 * - 로그아웃 시: localStorage의 notifyEnabled 값으로 notify-only socket 연결 여부 결정
 * - 설정 화면: getDevicePreference() / updateNotifyPreference() 로 토글 제어
 */
import { apiFetch } from './apiClient';
import { isTauri } from '../utils/tauri';

const NOTIFY_ENABLED_KEY = 'tauriNotifyEnabled';
const DEVICE_ID_KEY = 'tauriDeviceId';

export interface DevicePreference {
  deviceId: string;
  userId: string;
  platform: string;
  notifyEnabled: boolean;
  lastLoginAt: string;
}

/**
 * Tauri 환경에서 device_id 조회 (Rust가 영속 저장)
 * 로그인 후에는 localStorage에도 캐시해 두어 로그아웃 직후에도 접근 가능.
 */
export async function getDeviceId(): Promise<string | null> {
  if (!isTauri()) return null;
  try {
    const { invoke } = await import('@tauri-apps/api/core');
    const id = await invoke<string>('get_or_create_device_id');
    if (id) localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch (err) {
    console.warn('[deviceService] getDeviceId failed, falling back to localStorage:', err);
    return localStorage.getItem(DEVICE_ID_KEY);
  }
}

/**
 * 캐시된 device_id (로그아웃 후 동기 호출용)
 */
export function getCachedDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_KEY);
}

/**
 * 캐시된 notify_enabled 값 (로그아웃 시 즉시 판단용)
 * 기본값 true (한 번도 로그인 안 한 경우엔 알림 OFF 처리되어야 하므로 false 반환)
 */
export function getCachedNotifyEnabled(): boolean {
  const v = localStorage.getItem(NOTIFY_ENABLED_KEY);
  return v === 'true';
}

/**
 * 로그인 직후 device 등록 (또는 user_id 갱신)
 * 응답의 notifyEnabled를 localStorage에 캐시.
 */
export async function registerDevice(
  deviceId: string,
  platform: string
): Promise<{ ok: boolean; notifyEnabled: boolean }> {
  const res = await apiFetch<{ ok: boolean; notifyEnabled: boolean }>('/devices/register', {
    method: 'POST',
    body: JSON.stringify({ deviceId, platform }),
  });
  localStorage.setItem(NOTIFY_ENABLED_KEY, String(res.notifyEnabled !== false));
  return res;
}

/**
 * device 등록 정보 조회 (설정 화면에서 사용 - JWT 필요)
 */
export async function getDevicePreference(deviceId: string): Promise<DevicePreference> {
  return apiFetch<DevicePreference>(`/devices/${encodeURIComponent(deviceId)}`);
}

/**
 * 알림 ON/OFF 토글 (설정 화면에서 사용 - JWT 필요)
 */
export async function updateNotifyPreference(
  deviceId: string,
  notifyEnabled: boolean
): Promise<void> {
  await apiFetch<{ ok: boolean; notifyEnabled: boolean }>(
    `/devices/${encodeURIComponent(deviceId)}/preference`,
    {
      method: 'PATCH',
      body: JSON.stringify({ notifyEnabled }),
    }
  );
  localStorage.setItem(NOTIFY_ENABLED_KEY, String(notifyEnabled));
}

/**
 * 현재 platform 문자열 (백엔드 device_tokens.platform 컬럼용)
 */
export function getPlatform(): string {
  if (!isTauri()) return 'web';
  const ua = navigator.userAgent || '';
  if (ua.includes('Windows')) return 'tauri-windows';
  if (ua.includes('Mac')) return 'tauri-mac';
  if (ua.includes('Linux')) return 'tauri-linux';
  return 'tauri-unknown';
}
